import { useState, useEffect, useMemo, useCallback } from "react";
import { ArrowUpRight, ArrowLeft, Loader2, CheckCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import StepIndicator from "@/components/contact/StepIndicator";
import StepAboutYou from "@/components/contact/StepAboutYou";
import StepYourProject from "@/components/contact/StepYourProject";
import StepBoringStuff from "@/components/contact/StepBoringStuff";
import StepFunPart from "@/components/contact/StepFunPart";
import StepLocalsOnly from "@/components/contact/StepLocalsOnly";

const BASIC_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOTAL_STEPS = 5;

interface ContactSheetProps {
  children: React.ReactNode;
  defaultProjectType?: string;
  defaultDallasLocal?: boolean;
  defaultDallasOfferType?: string;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
};

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

/** The actual form content — shared between Sheet and BottomSheet */
const ContactFormContent = ({
  onClose,
  onViewOfferDetails,
  defaultProjectType = "",
  defaultDallasLocal = false,
  defaultDallasOfferType = "",
}: {
  onClose: () => void;
  onViewOfferDetails?: (offerType: string) => void;
  defaultProjectType?: string;
  defaultDallasLocal?: boolean;
  defaultDallasOfferType?: string;
}) => {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    elevatorPitch: "",
    projectType: defaultProjectType,
    budget: "",
    timeline: "",
    message: "",
    links: "",
  });
  const [isDallasLocal, setIsDallasLocal] = useState(defaultDallasLocal);
  const [dallasOfferType, setDallasOfferType] = useState(defaultDallasOfferType);
  const [tradeOffer, setTradeOffer] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [desiredFeatures, setDesiredFeatures] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [emailWarning, setEmailWarning] = useState(false);
  const [emailOverridden, setEmailOverridden] = useState(false);

  const update = useCallback((field: string, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (field === "email") {
      setEmailWarning(false);
      setEmailOverridden(false);
    }
  }, []);

  const hasPreselection = !!(defaultProjectType || defaultDallasOfferType);
  const [preselectionDismissed, setPreselectionDismissed] = useState(false);

  const isStep1Valid = useMemo(() => {
    const hasName = form.name.trim().length > 0;
    const hasEmail = form.email.trim().length > 0;
    if (!hasName || !hasEmail) return false;
    if (!BASIC_EMAIL_RE.test(form.email.trim()) && !emailOverridden) return false;
    return true;
  }, [form.name, form.email, emailOverridden]);

  const isStep5Valid = useMemo(
    () => form.message.trim().length > 0,
    [form.message]
  );

  const isStep2Valid = useMemo(() => {
    // Must answer yes or no
    if (!isDallasLocal && dallasOfferType === "__no__") return true; // answered "No"
    if (isDallasLocal && dallasOfferType && dallasOfferType !== "__no__") return true; // answered "Yes" + picked offer
    return false;
  }, [isDallasLocal, dallasOfferType]);

  const canProceed = step === 1 ? isStep1Valid : step === 2 ? isStep2Valid : step === 5 ? isStep5Valid : true;

  const goNext = () => {
    if (step === 1 && !BASIC_EMAIL_RE.test(form.email.trim()) && !emailOverridden) {
      setEmailWarning(true);
      return;
    }
    if (step < TOTAL_STEPS) {
      setDirection(1);
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep((s) => s - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isStep5Valid) return;
    setStatus("sending");

    try {
      // Upload files first
      const fileUrls: string[] = [];
      for (const file of files) {
        const filePath = `${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("contact-attachments")
          .upload(filePath, file);
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("contact-attachments")
            .getPublicUrl(filePath);
          fileUrls.push(urlData.publicUrl);
        }
      }

      // Save to database as backup
      const submissionData = {
        name: form.name,
        email: form.email,
        company: form.company || null,
        project_type: form.projectType || null,
        budget: form.budget || null,
        timeline: form.timeline || null,
        message: form.message,
        links: form.links || null,
        file_urls: fileUrls.length > 0 ? fileUrls : null,
        desired_features: desiredFeatures.length > 0 ? desiredFeatures : null,
        email_sent: false,
        is_dallas_local: isDallasLocal,
        dallas_offer_type: isDallasLocal ? dallasOfferType || null : null,
        trade_offer: isDallasLocal && dallasOfferType === "Barter / Trade" ? tradeOffer || null : null,
      };

      const { data: inserted } = await supabase
        .from("contact_submissions")
        .insert(submissionData)
        .select("id")
        .single();

      // Try sending email
      const { error } = await supabase.functions.invoke("send-contact", {
        body: {
          ...form,
          fileUrls,
          desiredFeatures,
          isDallasLocal,
          dallasOfferType: isDallasLocal ? dallasOfferType : undefined,
          tradeOffer: isDallasLocal && dallasOfferType === "Barter / Trade" ? tradeOffer : undefined,
        },
      });

      // Mark email_sent if successful
      if (!error && inserted?.id) {
        await supabase
          .from("contact_submissions")
          .update({ email_sent: true })
          .eq("id", inserted.id);
      }

      // Even if email fails, submission is saved — treat as success
      trackEvent("contact_submit", {
        project_type: form.projectType,
        budget: form.budget,
      });
      setStatus("sent");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <CheckCircle size={28} className="text-primary mb-3" />
        <h3 className="font-display text-xl italic text-foreground mb-1.5">
          Thank you!
        </h3>
        <p className="font-body text-ts-2xs text-muted-foreground mb-6">
          I'll review your inquiry and get back to you soon.
        </p>
        <button
          onClick={onClose}
          className="px-5 py-2 border border-foreground text-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase hover:bg-foreground hover:text-background transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Step indicator */}
      <div className="px-6 pt-4 pb-2">
        <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />
      </div>

      {/* Preselection banner */}
      {hasPreselection && !preselectionDismissed && (
        <div className="mx-6 mt-1 mb-0 flex items-center gap-2 bg-muted/50 px-3 py-2">
          <p className="font-body text-[11px] text-muted-foreground flex-1">
            A service/offer has been preselected for you.{" "}
            <button
              type="button"
              onClick={() => {
                setDirection(1);
                setStep(3);
              }}
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
            >
              Change
            </button>
          </p>
          <button
            type="button"
            onClick={() => setPreselectionDismissed(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Step content with slide animation */}
      <div className="flex-1 overflow-y-auto relative">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {step === 1 && (
              <StepAboutYou
                form={form}
                update={update}
                emailWarning={emailWarning}
                setEmailWarning={setEmailWarning}
                emailOverridden={emailOverridden}
                setEmailOverridden={setEmailOverridden}
              />
            )}
            {step === 2 && (
              <StepLocalsOnly
                isDallasLocal={isDallasLocal}
                setIsDallasLocal={setIsDallasLocal}
                dallasOfferType={dallasOfferType}
                setDallasOfferType={setDallasOfferType}
                onViewOfferDetails={onViewOfferDetails}
              />
            )}
            {step === 3 && (
              <StepYourProject
                projectType={form.projectType}
                updateProjectType={(v) => update("projectType", v)}
              />
            )}
            {step === 4 && (
              <StepBoringStuff
                budget={form.budget}
                timeline={form.timeline}
                update={update}
              />
            )}
            {step === 5 && (
              <StepFunPart
                message={form.message}
                links={form.links}
                files={files}
                desiredFeatures={desiredFeatures}
                projectType={form.projectType}
                update={update}
                setFiles={setFiles}
                setDesiredFeatures={setDesiredFeatures}
                isDallasLocal={isDallasLocal}
                dallasOfferType={dallasOfferType}
                tradeOffer={tradeOffer}
                setTradeOffer={setTradeOffer}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Error message */}
      {status === "error" && (
        <p className="px-6 font-body text-ts-2xs text-destructive">
          Something went wrong. Please try again or email me directly at
          madi@keish.studio.
        </p>
      )}

      {/* Sticky footer */}
      <div className="border-t border-border px-6 py-4 bg-background flex items-center gap-3">
        {step > 1 && (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 px-4 py-3 border border-border font-body text-ts-2xs font-medium tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
          >
            <ArrowLeft size={12} />
            Back
          </button>
        )}

        {step < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canProceed}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors ${
              canProceed
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted-foreground/20 text-muted-foreground cursor-not-allowed"
            }`}
          >
            Next
            <ArrowUpRight size={12} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={status === "sending" || !canProceed}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors ${
              canProceed
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted-foreground/20 text-muted-foreground cursor-not-allowed"
            }`}
          >
            {status === "sending" ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Sending…
              </>
            ) : canProceed ? (
              <>
                Submit Form
                <ArrowUpRight size={12} />
              </>
            ) : (
              "Complete Req. Fields"
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const ContactSheet = ({
  children,
  defaultProjectType,
  defaultDallasLocal,
  defaultDallasOfferType,
  externalOpen,
  onExternalOpenChange,
}: ContactSheetProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isMobile = useIsMobile();

  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = (val: boolean) => {
    if (isControlled) {
      onExternalOpenChange?.(val);
    } else {
      setInternalOpen(val);
    }
  };

  const handleClose = () => setOpen(false);
  const handleOpen = (val: boolean) => {
    setOpen(val);
    if (val) trackEvent("contact_open");
  };

  const handleViewOfferDetails = (offerType: string) => {
    handleClose();
    setTimeout(() => {
      const el = document.getElementById("dallas-locals");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
      // Give scroll time to settle, then dispatch event to open the offer
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-dallas-offer", { detail: offerType }));
      }, 600);
    }, 300);
  };

  // Desktop: sidebar Sheet
  if (!isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpen}>
        <SheetTrigger asChild>{children}</SheetTrigger>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col bg-background border-l border-border"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border text-left">
            <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-1">
              06 — Contact
            </p>
            <SheetTitle className="font-heading text-ts-xl text-foreground">
              Start a Project
            </SheetTitle>
            <SheetDescription className="font-body text-ts-xs text-foreground/60 leading-relaxed mt-1">
              Tell me about your project and I'll get back to you within 24 hours.
            </SheetDescription>
          </SheetHeader>
          <ContactFormContent
            onClose={handleClose}
            onViewOfferDetails={handleViewOfferDetails}
            defaultProjectType={defaultProjectType}
            defaultDallasLocal={defaultDallasLocal}
            defaultDallasOfferType={defaultDallasOfferType}
          />
        </SheetContent>
      </Sheet>
    );
  }

  // Mobile/Tablet: bottom sheet
  return (
    <>
      <span
        onClick={() => {
          setOpen(true);
          trackEvent("contact_open");
        }}
      >
        {children}
      </span>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={handleClose}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border rounded-t-xl flex flex-col text-left"
              style={{
                maxHeight: "92dvh",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              {/* Handle + header */}
              <div className="px-5 pt-3 pb-4 border-b border-border">
                <div className="w-10 h-1 rounded-full bg-muted-foreground/20 mx-auto mb-4" />
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-1">
                      06 — Contact
                    </p>
                    <h2 className="font-heading text-ts-xl text-foreground">
                      Start a Project
                    </h2>
                    <p className="font-body text-ts-xs text-foreground/60 leading-relaxed mt-1">
                      Tell me about your project and I'll get back to you
                      within 24 hours.
                    </p>
                    <p className="font-body text-ts-2xs text-muted-foreground mt-2">
                      Or just{" "}
                      <a
                        href="mailto:madi@keish.studio"
                        className="link-dotted"
                      >
                        email us directly
                      </a>
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors mt-1"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
              <ContactFormContent
                onClose={handleClose}
                onViewOfferDetails={handleViewOfferDetails}
                defaultProjectType={defaultProjectType}
                defaultDallasLocal={defaultDallasLocal}
                defaultDallasOfferType={defaultDallasOfferType}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactSheet;
